import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { getProjectLimit } from "../../../controller/global.function.library.js";
import { getUserPlan } from "../../../api/users.api.js";
import { cancelSubscription } from "../../../api/services/stripeService.js";

import { UserContext } from "../../../model/contexts/user.context";
import { ProjectContext } from "../../../model/contexts/project.context";

import NavBar from "../../components/NavBar/NavBar";
import SideBar from "../../components/SideBar/SideBar";
import UpgradeButton from "../../components/UpgradePlanButton/UpgradePlanButton.jsx";

import "./Billing.scss";

const Billing = () => {
  const { currentUser } = useContext(UserContext);
  const { countUserProjects } = useContext(ProjectContext);

  const [userPlan, setUserPlan] = useState("freemium");
  const [projectCountValue, setProjectCountValue] = useState(0);

  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingModalTitle, setBillingModalTitle] = useState("");
  const [billingModalMessage, setBillingModalMessage] = useState("");

  const projectLimit = getProjectLimit(userPlan) || 0;
  const remaining = Math.max(0, projectLimit - projectCountValue);

  const navigate = useNavigate();

  const capitalizePlan = (plan) => {
    if (!plan) return "Freemium";
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  useEffect(() => {
    if (!currentUser) return;
    setUserPlan(currentUser?.plan || "freemium");
  }, [currentUser]);

  useEffect(() => {
    const loadProjectCount = async () => {
      if (!currentUser) return;

      try {
        const count = await countUserProjects();

        setProjectCountValue(count || 0);
      } catch (error) {
        console.error(
          "[BILLING] Error loading project count:",
          error?.response?.data || error.message
        );
      }
    };

    loadProjectCount();
  }, [currentUser]);

  useEffect(() => {
    const checkForBillingChange = async () => {
      if (!currentUser?.uid) return;

      try {
        const previousPlan = userPlan || "freemium";

        const latestUser = await getUserPlan(currentUser.uid);
        const latestPlan = latestUser?.plan || "freemium";

        // console.log("[BILLING] Latest plan check:", {
        //   previousPlan,
        //   latestPlan,
        //   currentUserPlan: currentUser?.plan,
        // });

        if (latestPlan !== previousPlan) {
          // console.log(
          //   "[BILLING MODAL TRIGGERED]",
          //   JSON.stringify({
          //     previousPlan,
          //     latestPlan,
          //     uid: currentUser.uid,
          //     timestamp: new Date().toISOString(),
          //   })
          // );

          setUserPlan(latestPlan);

          setBillingModalTitle("Subscription Updated");
          setBillingModalMessage(
            `Your plan has changed from ${capitalizePlan(
              previousPlan
            )} to ${capitalizePlan(
              latestPlan
            )}. Your project access and billing limits have been updated.`
          );
          setBillingModalOpen(true);
        } else {
          setUserPlan(latestPlan);
        }
      } catch (error) {
        console.error(
          "[BILLING] Error checking latest billing plan:",
          error?.response?.data || error.message
        );
      }
    };

    checkForBillingChange();
  }, [currentUser?.uid]);

  const handleDowngrade = async (plan) => {
    if (!currentUser?.uid) return;

    try {
      // console.log("[BILLING DOWNGRADE START]", {
      //   uid: currentUser.uid,
      //   fromPlan: userPlan,
      //   toPlan: plan,
      // });

      const response = await cancelSubscription({
        uid: currentUser.uid,
      });

      // console.log("[BILLING DOWNGRADE SUCCESS]", response);

      setUserPlan("freemium");

      setBillingModalTitle("Subscription Downgraded");
      setBillingModalMessage(
        "Your paid subscription has been cancelled and your account has been moved back to Freemium."
      );
      setBillingModalOpen(true);
    } catch (error) {
      console.error(
        "[BILLING DOWNGRADE FAILED]",
        error?.response?.data || error.message
      );

      setBillingModalTitle("Downgrade Failed");
      setBillingModalMessage(
        "We were unable to cancel your subscription. Please try again."
      );
      setBillingModalOpen(true);
    }
  };

  return (
    <div className="settings-outer-container">
      <NavBar />
      Settings - User Profile
      <div className="settings-outer-content-container">
        <div className="sidebar-container">
          <SideBar />
        </div>
        <div className="user-profile-content-container">
          <div className="user-profile-header">
            <h2>User Plan</h2>
            <p>Manage your subscription.</p>
          </div>

          <div className="settings-section">
            <div className="settings-section-header">
              <h3>Plan & Billing</h3>
              <span className="settings-section-subtext">
                View your current plan and upgrade when needed.
              </span>
            </div>

            <div className="plan-card">
              <div className="plan-card-top">
                <div>
                  <p className="plan-card-label">Current Plan</p>
                  <h2 className="plan-card-title">{userPlan}</h2>
                </div>

                <div className="plan-badge">
                  {userPlan === "freemium" && "Freemium"}
                  {userPlan === "standard" && "Standard"}
                  {userPlan === "premium" && "Premium"}
                </div>
              </div>

              <div className="plan-usage-block">
                <div className="plan-usage-row">
                  <span>Projects Used</span>
                  <span>
                    {projectCountValue} / {projectLimit}
                  </span>
                </div>

                <div className="plan-usage-bar">
                  <div
                    className="plan-usage-bar-fill"
                    style={{
                      width: `${
                        projectLimit > 0
                          ? Math.min(
                              (projectCountValue / projectLimit) * 100,
                              100
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>

                <p className="plan-usage-message">
                  {projectCountValue >= projectLimit
                    ? "You’ve reached your current project limit. Upgrade to keep creating more projects."
                    : `${remaining} project${
                        remaining === 1 ? "" : "s"
                      } remaining on your current plan.`}
                </p>
              </div>

              <div className="plan-options-grid">
                <div
                  className={`plan-option-card ${
                    userPlan === "freemium" ? "active-plan" : ""
                  }`}
                >
                  <h4>Freemium</h4>
                  <p className="plan-price">$0/mo</p>
                  <p className="plan-meta">Up to 1 project</p>

                  <button
                    className="plan-action-button secondary-button"
                    disabled={userPlan === "freemium" || !currentUser?.uid}
                    onClick={() => handleDowngrade("freemium")}
                  >
                    {userPlan === "freemium"
                      ? "Current Plan"
                      : "Select Freemium"}
                  </button>
                </div>

                <div
                  className={`plan-option-card ${
                    userPlan === "standard" ? "active-plan" : ""
                  }`}
                >
                  <h4>Standard</h4>
                  <p className="plan-price">$5/mo</p>
                  <p className="plan-meta">Up to 300 projects</p>

                  <UpgradeButton
                    uid={currentUser?.uid}
                    plan="standard"
                    label={
                      userPlan === "standard"
                        ? "Current Plan"
                        : "Select Standard"
                    }
                    className="plan-action-button primary-button"
                    disabled={userPlan === "standard" || !currentUser?.uid}
                  />
                </div>

                <div
                  className={`plan-option-card ${
                    userPlan === "premium" ? "active-plan" : ""
                  }`}
                >
                  <h4>Premium</h4>
                  <p className="plan-price">$8/mo</p>
                  <p className="plan-meta">Up to 500 projects</p>

                  <UpgradeButton
                    uid={currentUser?.uid}
                    plan="premium"
                    label={
                      userPlan === "premium" ? "Current Plan" : "Select Premium"
                    }
                    className="plan-action-button primary-button"
                    disabled={userPlan === "premium" || !currentUser?.uid}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {billingModalOpen && (
        <div className="billing-modal-backdrop">
          <div className="billing-modal">
            <h3>{billingModalTitle}</h3>
            <p>{billingModalMessage}</p>

            <button
              className="plan-action-button primary-button"
              onClick={() => {
                // console.log("[BILLING MODAL CLOSED]");
                setBillingModalOpen(false);
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
